import { DashboardProfileData } from "../..";
import { useTranslation } from "react-i18next";
import { headerOffset } from "components/basic/header";
import { useTheme } from "@mui/material";
import { ProfileEditButton } from "../../components/ProfileEditButton";
import DeleteAccountButton from "../../components/DeleteAccountButton";
import { DEFAULT_PROFILE_TAB_HEIGHT } from "components/basic/tabs";
import { useUpdateAccountMutation } from "generated/graphql";
import { useSnack } from "hooks/useSnack";
import { formatPhoneNumber } from "pages/formTools";

interface PersonalProfileDashboardProps {
  dashboardProfileData: DashboardProfileData;
  onChange: (name: string, value: string | boolean) => void;
}

const PersonalProfileDashboard: React.FC<PersonalProfileDashboardProps> = ({
  dashboardProfileData,
  onChange,
}) => {
  const theme = useTheme();
  const { account } = dashboardProfileData;
  const showSnack = useSnack();
  const { t } = useTranslation("profile");
  const [, updateAccount] = useUpdateAccountMutation();

  // Resolves false on a rejected save so ProfileEditButton restores the old
  // value instead of showing one that was never stored.
  const handleFieldSave = async (
    updates: Record<string, string | boolean | number>,
  ): Promise<boolean> => {
    const response = await updateAccount({ options: updates });
    if (response.error || response.data?.updateAccount.errors) {
      showSnack(
        response.data?.updateAccount.errors?.[0].message ??
          response.error?.message ??
          t("error"),
        "error",
      );
      return false;
    }
    showSnack(t("saved"), "success");
    return true;
  };

  return (
    <div
      style={{
        display: "flex",
        overflow: "auto",
        scrollbarWidth: "thin",
        width: "100%",
        height: `calc(100vh - ${headerOffset(DEFAULT_PROFILE_TAB_HEIGHT + 1)})`,
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          alignItems: "center",
          justifyContent: "space-between",
          padding: theme.spacing(1.8),
          // Stretches to the parent's fixed viewport height, so the padding
          // has to sit inside it or it forces a scrollbar.
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            maxWidth: "560px",
            overflowY: "auto",
            scrollbarWidth: "thin",
          }}
        >
          <ProfileEditButton
            label={t("fields.profile_photo")}
            name="profilePhoto"
            input={account.profilePhotoUrl || ""}
            onChange={onChange}
            onSave={handleFieldSave}
            type="photo"
          />

          <ProfileEditButton
            label={t("fields.first_name")}
            name="firstName"
            input={account.firstName}
            onChange={onChange}
            onSave={handleFieldSave}
          />

          <ProfileEditButton
            label={t("fields.last_name")}
            name="lastName"
            input={account.lastName}
            onChange={onChange}
            onSave={handleFieldSave}
          />

          <ProfileEditButton
            label={t("fields.job")}
            name="jobTitle"
            input={account.jobTitle || ""}
            onChange={onChange}
            onSave={handleFieldSave}
          />

          <ProfileEditButton
            label={t("fields.phone")}
            name="phoneNumber"
            input={account.phoneNumber ?? ""}
            onChange={onChange}
            onSave={handleFieldSave}
            mask={formatPhoneNumber}
          />

          <ProfileEditButton
            label={t("fields.home_address")}
            name="address"
            input={account.address ?? ""}
            onChange={onChange}
            onSave={handleFieldSave}
            type="address"
          />

          <DeleteAccountButton />
        </div>
      </div>
    </div>
  );
};

export default PersonalProfileDashboard;
